import React, { useState, useEffect, useMemo } from 'react';
import styled, { keyframes } from 'styled-components';
import axios from 'axios';

// --- ANIMATIONS & SPINNER ---
const spin = keyframes`
  0% { transform: rotate(0deg); }
  100% { transform: rotate(360deg); }
`;

const Spinner = styled.div`
  width: 18px;
  height: 18px;
  border: 2px solid rgba(0, 0, 0, 0.1);
  border-top: 2px solid #0e141b;
  border-radius: 50%;
  animation: ${spin} 0.8s linear infinite;
  display: inline-block;
`;

// --- STYLED COMPONENTS ---
const PageContainer = styled.div`
  display: flex;
  flex-direction: column;
  width: 100%;
  min-height: 100vh;
  background-color: #f8fafc;
  padding: 20px;
  box-sizing: border-box;
`;

const HeaderSection = styled.div`
  display: flex;
  flex-direction: column;
  gap: 16px;
  margin-bottom: 20px;
`;

const TopRow = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  flex-wrap: wrap;
  gap: 12px;
`;

const BackBtn = styled.button`
  background: #e2e8f0;
  border: none;
  padding: 8px 16px;
  border-radius: 8px;
  cursor: pointer;
  font-weight: 600;
  color: #0e141b;
  transition: all 0.2s ease;
  &:hover {
    background: #cbd5e1;
  }
`;

const Title = styled.h2`
  color: #0d141c;
  font-size: 1.5rem;
  font-weight: 700;
  margin: 0;
`;

const ControlsGroup = styled.div`
  display: flex;
  align-items: center;
  gap: 10px;
  flex-wrap: wrap;
`;

const TabButton = styled.button`
  background: ${(props) => (props.$active ? '#0e141b' : 'white')};
  color: ${(props) => (props.$active ? '#ffffff' : '#475569')};
  border: 1px solid ${(props) => (props.$active ? '#0e141b' : '#cbd5e1')};
  padding: 8px 16px;
  border-radius: 20px;
  cursor: pointer;
  font-weight: 600;
  font-size: 0.85rem;
  transition: all 0.2s ease;
  text-transform: capitalize;

  &:hover {
    border-color: #0e141b;
  }
`;

const SearchInput = styled.input`
  padding: 8px 14px;
  border-radius: 8px;
  border: 1px solid #cbd5e1;
  font-size: 0.875rem;
  outline: none;
  width: 100%;
  max-width: 300px;
  background: white;

  &:focus {
    border-color: #0e141b;
  }
`;

const TableCard = styled.div`
  background: white;
  border: 1px solid #e2e8f0;
  border-radius: 12px;
  box-shadow: 0 4px 6px -1px rgba(0, 0, 0, 0.05);
  overflow: hidden;
  width: 100%;
`;

const TableHeader = styled.div`
  display: grid;
  grid-template-columns: 2fr 1fr 2fr;
  padding: 12px 20px;
  background-color: #f1f5f9;
  border-bottom: 1px solid #e2e8f0;
  font-weight: 700;
  font-size: 0.75rem;
  color: #475569;
  text-transform: uppercase;
  letter-spacing: 0.05em;

  @media (max-width: 600px) {
    grid-template-columns: 1.5fr 1fr;
  }
`;

const TableBody = styled.div`
  display: flex;
  flex-direction: column;
  max-height: 65vh;
  overflow-y: auto;
`;

const TableRow = styled.div`
  display: grid;
  grid-template-columns: 2fr 1fr 2fr;
  align-items: center;
  padding: 12px 20px;
  border-bottom: 1px solid #f1f5f9;
  transition: background-color 0.15s ease;

  &:hover {
    background-color: #f8fafc;
  }

  &:last-child {
    border-bottom: none;
  }

  @media (max-width: 600px) {
    grid-template-columns: 1.5fr 1fr;
  }
`;

const FeatureName = styled.span`
  font-size: 0.875rem;
  font-weight: 600;
  color: #0f172a;
`;

const FeatureVal = styled.span`
  font-size: 0.875rem;
  font-weight: 700;
  color: #0e141b;
`;

const BarContainer = styled.div`
  width: 100%;
  background: #f1f5f9;
  height: 8px;
  border-radius: 4px;
  overflow: hidden;
  position: relative;

  @media (max-width: 600px) {
    display: none;
  }
`;

const BarFill = styled.div`
  height: 100%;
  background: ${(props) => props.$color || '#0e141b'};
  width: ${(props) => props.$pct}%;
  transition: width 0.3s ease;
  border-radius: 4px;
`;

const EmptyState = styled.div`
  display: flex;
  justify-content: center;
  align-items: center;
  padding: 40px;
  color: #64748b;
  font-size: 0.9rem;
`;

// --- HELPER UTILS ---
function shortenValue(val) {
  if (typeof val === 'string' && isNaN(val)) return val;
  return Number(val).toFixed(2);
}

const GEMS = ["ruby", "rock", "diamond", "quartz", "beryl", "mica", "onyx", "pearl", "garnet", "zircon", "zinc"];
const VIEWS = ['points', 'assists', 'rebounds'];

const GEM_COLORS = {
  rock: '#4f46e5',
  ruby: '#e11d48',
  diamond: '#38bdf8',
  quartz: '#ec4899',
  beryl: '#10b981',
  mica: '#f59e0b',
  onyx: '#334155',
  pearl: '#e2e8f0',
  garnet: '#9f1239',
  zircon: '#06b6d4',
  zinc: '#94a3b8',
};

// Keys to omit from table view
const IGNORED_METRICS = new Set(['y_train_range', 'y_train_std', 'y_test_range']);

// --- COMPONENT ---
export const Features = ({ onBack }) => {
  const [activeView, setActiveView] = useState('points');
  const [activeGem, setActiveGem] = useState('ruby');
  const [searchQuery, setSearchQuery] = useState('');
  
  // Feature Store: { "points_ruby": [...], "points_rock": [...] }
  const [allFeatureStore, setAllFeatureStore] = useState({});
  const [pendingCount, setPendingCount] = useState(0);

  // Helper to fetch individual combination
  const fetchSingleCombo = async (targetView, gem) => {
    try {
      const res = await axios({
        method: 'post',
        url: 'https://sx7vpiy3ij.execute-api.us-east-2.amazonaws.com/admin_dev/team-pull',
        withCredentials: false,
        data: {
          option: 'features',
          target: targetView,
          gem,
        },
      });
      return res?.data?.data || [];
    } catch (err) {
      console.error(`Error fetching features for ${targetView}_${gem}`, err);
      return [];
    }
  };

  // Fire off all requests immediately on mount
  useEffect(() => {
    let isSubscribed = true;

    const totalRequests = GEMS.length * VIEWS.length;
    setPendingCount(totalRequests);

    GEMS.forEach((g) => {
      VIEWS.forEach((v) => {
        fetchSingleCombo(v, g).then((data) => {
          if (!isSubscribed) return;

          setAllFeatureStore((prev) => ({
            ...prev,
            [`${v}_${g}`]: data,
          }));

          setPendingCount((cnt) => Math.max(0, cnt - 1));
        });
      });
    });

    return () => {
      isSubscribed = false;
    };
  }, []);

  // Retrieve current active view dataset instantly if ready
  const activeKey = `${activeView}_${activeGem}`;
  const isCurrentKeyLoaded = activeKey in allFeatureStore;
  const currentFeatures = useMemo(() => {
    return allFeatureStore[activeKey] || [];
  }, [allFeatureStore, activeKey]);

  // Max value for visual progress bars (calculated post-filtering)
  const maxVal = useMemo(() => {
    if (!currentFeatures.length) return 1;
    const nums = currentFeatures
      .filter(([key]) => !IGNORED_METRICS.has(String(key)))
      .map(([, val]) => Math.abs(Number(val)))
      .filter((v) => !isNaN(v));
    return Math.max(...nums, 1);
  }, [currentFeatures]);

  // Search filter & exclusion logic
  const filteredFeatures = useMemo(() => {
    if (!currentFeatures.length) return [];

    return currentFeatures.filter(([key]) => {
      const metricKey = String(key);
      
      // Exclude unwanted metrics
      if (IGNORED_METRICS.has(metricKey)) {
        return false;
      }

      // Apply search query
      if (searchQuery.trim()) {
        return metricKey.toLowerCase().includes(searchQuery.toLowerCase());
      }

      return true;
    });
  }, [currentFeatures, searchQuery]);

  return (
    <PageContainer>
      <HeaderSection>
        <TopRow>
          <div style={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
            {onBack && <BackBtn onClick={onBack}>← Back</BackBtn>}
            <Title>Model Features Overview</Title>
            {pendingCount > 0 && <Spinner title="Loading remaining datasets..." />}
          </div>

          <ControlsGroup>
            {/* Gem Selector */}
            {GEMS.map((g) => (
              <TabButton
                key={g}
                $active={activeGem === g}
                onClick={() => setActiveGem(g)}
              >
                {g}
              </TabButton>
            ))}
          </ControlsGroup>
        </TopRow>

        <TopRow>
          {/* View Category Tabs */}
          <ControlsGroup>
            {VIEWS.map((v) => (
              <TabButton
                key={v}
                $active={activeView === v}
                onClick={() => setActiveView(v)}
              >
                {v}
              </TabButton>
            ))}
          </ControlsGroup>

          {/* Search Input */}
          <SearchInput
            type="text"
            placeholder="Search metric name..."
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
          />
        </TopRow>
      </HeaderSection>

      <TableCard>
        <TableHeader>
          <div>Feature Name</div>
          <div>Value</div>
          <div>Relative Scale</div>
        </TableHeader>

        <TableBody>
          {!isCurrentKeyLoaded ? (
            <EmptyState>
              <div style={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
                <Spinner /> Loading {activeView} ({activeGem})...
              </div>
            </EmptyState>
          ) : filteredFeatures.length === 0 ? (
            <EmptyState>
              {searchQuery ? 'No matching features found.' : 'No feature data available.'}
            </EmptyState>
          ) : (
            filteredFeatures.map(([metricKey, val]) => {
              const numVal = Number(val);
              const pct = isNaN(numVal)
                ? 0
                : Math.min(100, (Math.abs(numVal) / maxVal) * 100);

              return (
                <TableRow key={metricKey}>
                  <FeatureName>{metricKey}</FeatureName>
                  <FeatureVal>{shortenValue(val)}</FeatureVal>
                  <BarContainer>
                    <BarFill
                      $pct={pct}
                      $color={GEM_COLORS[activeGem] || '#0e141b'}
                    />
                  </BarContainer>
                </TableRow>
              );
            })
          )}
        </TableBody>
      </TableCard>
    </PageContainer>
  );
};

export default Features;